import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineLockClosed } from "react-icons/hi2";
import { getFeatureCopy } from "../PlanGate/featureCopy";
import { usePlanOffer } from "../PlanGate/usePlanOffer";

/**
 * Aviso global de función no incluida en el plan (spec 007, FR-014).
 * Vive montado en MenuWrapper (mismo patrón que PlanLimitPrompt) y escucha el
 * evento "plan-feature-locked" que disparan los interceptores de axios al
 * recibir 403 code=PLAN_FEATURE_LOCKED. Sin esto la vista gateada queda en un
 * error seco y el usuario no entiende por qué no carga.
 *
 * Nombre y plan salen del catálogo único (components/PlanGate/featureCopy).
 * Las pantallas completas se bloquean antes con <PlanGate>; esto cubre las
 * llamadas sueltas a endpoints gateados desde otras vistas.
 */
const PlanFeatureLocked = () => {
  const [detail, setDetail] = useState<{
    feature?: string;
    requiredPlan?: string;
  } | null>(null);
  const navigate = useNavigate();
  const { offer, buyPlan } = usePlanOffer(detail != null);

  useEffect(() => {
    const onLocked = (e: Event) => setDetail((e as CustomEvent).detail ?? {});
    window.addEventListener("plan-feature-locked", onLocked);
    return () => window.removeEventListener("plan-feature-locked", onLocked);
  }, []);

  if (!detail) return null;

  const copy = detail.feature ? getFeatureCopy(detail.feature) : null;
  const featureLabel = copy && copy.name !== "Esta función" ? copy.name : "Esta función";
  const requiredPlan = detail.requiredPlan ?? copy?.requiredPlan ?? "Estándar";

  return (
    <div
      role="alertdialog"
      aria-label="Función no incluida en tu plan"
      onClick={() => setDetail(null)}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        background: "rgba(23, 15, 60, 0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "16px",
          maxWidth: "440px",
          width: "100%",
          padding: "2.25rem 2rem",
          textAlign: "center",
          boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
          fontFamily: '"Mulish", sans-serif',
        }}
      >
        <div
          aria-hidden
          style={{
            width: 56,
            height: 56,
            margin: "0 auto 0.9rem",
            borderRadius: 16,
            background: "#f1edff",
            color: "#45348E",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <HiOutlineLockClosed size={28} />
        </div>
        <h2
          style={{ margin: "0 0 0.5rem", color: "#45348E", fontSize: "1.25rem" }}
        >
          {featureLabel} viene con el plan {requiredPlan}
        </h2>
        <p style={{ margin: "0 0 1.5rem", color: "#4b5563", lineHeight: 1.5 }}>
          {copy && copy.name !== "Esta función"
            ? copy.subtitle
            : "Tu plan actual no incluye esta función."}{" "}
          {offer.canPurchase
            ? `Pasa al plan ${requiredPlan} y empieza a usarla hoy mismo.`
            : `Pídele al administrador de tu negocio que active el plan ${requiredPlan}.`}
        </p>
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {offer.canPurchase && (
            <button
              onClick={() => {
                setDetail(null);
                buyPlan(requiredPlan);
              }}
              style={{
                background: "linear-gradient(90deg, #937cf4, #6f57e0)",
                color: "#fff",
                border: "none",
                borderRadius: "10px",
                padding: "0.8rem 1.5rem",
                fontSize: "0.95rem",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Pasar a {requiredPlan}
            </button>
          )}
          <button
            onClick={() => {
              setDetail(null);
              navigate("/mi-cuenta/planes");
            }}
            style={{
              background: "transparent",
              color: "#45348E",
              border: "1px solid #937cf4",
              borderRadius: "10px",
              padding: "0.8rem 1.5rem",
              fontSize: "0.95rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Comparar planes
          </button>
          <button
            onClick={() => setDetail(null)}
            style={{
              background: "transparent",
              color: "#4b5563",
              border: "none",
              padding: "0.8rem 1rem",
              fontSize: "0.95rem",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanFeatureLocked;
